import { validProductionDocument, productionDocumentClock, type ProductionDocument, type NoteValue, type NoteRegister } from "../../server/production-notes.mjs";
export { validProductionDocument, mergeProductionDocuments, productionDocumentClock, MAX_NOTES_BYTES, MAX_NOTES_RECORDS, NOTES_ID_RE } from "../../server/production-notes.mjs";
export type { ProductionDocument, ProductionRecord, NoteValue, NoteRegister } from "../../server/production-notes.mjs";
export const NOTES_STORAGE_PREFIX = "cue-studio-production-notes-v1:";
export function createProductionDocument(projectId: string, projectName: string, actor: string): ProductionDocument {
  const doc: ProductionDocument = { version: 1, projectId, fields: { projectName: { value: projectName, clock: 1, actor } }, records: {} };
  if (!validProductionDocument(doc)) throw new Error("Invalid production project.");
  return doc;
}
export function editProductionDocument(doc: ProductionDocument, actor: string, changes: {
  fields?: Record<string, NoteValue>; records?: Record<string, Record<string, NoteValue>>;
}): ProductionDocument {
  const clock = productionDocumentClock(doc) + 1;
  const registers = (fields: Record<string, NoteValue>) => Object.fromEntries(Object.entries(fields).map(([key, value]) => [key, { value, clock, actor }]));
  const next: ProductionDocument = { ...doc, fields: { ...doc.fields, ...registers(changes.fields || {}) }, records: { ...doc.records } };
  for (const [id, fields] of Object.entries(changes.records || {})) next.records[id] = { fields: { ...doc.records[id]?.fields, ...registers(fields) } };
  if (!validProductionDocument(next)) throw new Error("Production notes are too large or contain invalid fields. Export this project before starting another.");
  return next;
}
export function readField<T extends NoteValue>(fields: Record<string, NoteRegister>, key: string, fallback: T): T {
  const value = fields[key]?.value;
  return (value === undefined ? fallback : value) as T;
}
export function visibleProductionRecords(doc: ProductionDocument) {
  return Object.entries(doc.records).filter(([, record]) => record.fields.deleted?.value !== true)
    .map(([id, record]) => ({ id, ...record }));
}
export function loadProductionDocument(projectId: string): ProductionDocument | null {
  try {
    const raw = localStorage.getItem(NOTES_STORAGE_PREFIX + projectId);
    if (!raw) return null;
    const doc: unknown = JSON.parse(raw);
    return validProductionDocument(doc) && doc.projectId === projectId ? doc : null;
  } catch { return null; }
}
export function saveProductionDocument(doc: ProductionDocument): void {
  if (!validProductionDocument(doc)) throw new Error("Invalid production notes.");
  // Let quota/storage errors reach the UI: never claim an unsaved edit is safe.
  localStorage.setItem(NOTES_STORAGE_PREFIX + doc.projectId, JSON.stringify(doc));
}
